import { http } from '@/app/lib/http/client'

// ─── Types ──────────────────────────────────────────────────────────────────

export interface ShippingCoverageRule {
    id: number
    coverage_id: number
    postal_code: string
    minimum_purchase: string
    is_active: boolean
    created_at: string
    updated_at: string
}

export interface ShippingCoverage {
    id: number
    company_id: number
    sucursal_id: number
    name: string
    is_active: boolean
    created_at: string
    updated_at: string
    deleted_at: string | null
    rules?: ShippingCoverageRule[]
    rules_count?: number
}

export interface CreateCoveragePayload {
    name: string
    is_active?: boolean
}

export interface UpdateCoveragePayload {
    name?: string
    is_active?: boolean
}

export interface CreateRulePayload {
    postal_code: string
    minimum_purchase?: number
    is_active?: boolean
}

export interface UpdateRulePayload {
    postal_code?: string
    minimum_purchase?: number
    is_active?: boolean
}

export interface ImportResult {
    processed: number
    created: number
    updated: number
    skipped: number
    errors: Record<number, string>
}

// ─── Service ─────────────────────────────────────────────────────────────────

export const shippingCoverageService = {
    // ── Coberturas ────────────────────────────────────────────────────────────

    async getCoverages(sucursalId: number, withRules = false): Promise<ShippingCoverage[]> {
        const response = await http.get(`/sucursales/${sucursalId}/coverages`, {
            params: { with_rules: withRules ? 1 : undefined },
        })
        return response.data?.data ?? response.data
    },

    async getCoverage(sucursalId: number, coverageId: number): Promise<ShippingCoverage> {
        const response = await http.get(`/sucursales/${sucursalId}/coverages/${coverageId}`)
        return response.data?.data ?? response.data
    },

    async createCoverage(sucursalId: number, payload: CreateCoveragePayload): Promise<ShippingCoverage> {
        const response = await http.post(`/sucursales/${sucursalId}/coverages`, payload)
        return response.data?.data ?? response.data
    },

    async updateCoverage(
        sucursalId: number,
        coverageId: number,
        payload: UpdateCoveragePayload,
    ): Promise<ShippingCoverage> {
        const response = await http.patch(`/sucursales/${sucursalId}/coverages/${coverageId}`, payload)
        return response.data?.data ?? response.data
    },

    async deleteCoverage(sucursalId: number, coverageId: number): Promise<void> {
        await http.delete(`/sucursales/${sucursalId}/coverages/${coverageId}`)
    },

    // ── Reglas (Códigos Postales) ─────────────────────────────────────────────

    async createRule(
        sucursalId: number,
        coverageId: number,
        payload: CreateRulePayload,
    ): Promise<ShippingCoverageRule> {
        const response = await http.post(
            `/sucursales/${sucursalId}/coverages/${coverageId}/rules`,
            payload,
        )
        return response.data?.data ?? response.data
    },

    async updateRule(
        sucursalId: number,
        coverageId: number,
        ruleId: number,
        payload: UpdateRulePayload,
    ): Promise<ShippingCoverageRule> {
        const response = await http.patch(
            `/sucursales/${sucursalId}/coverages/${coverageId}/rules/${ruleId}`,
            payload,
        )
        return response.data?.data ?? response.data
    },

    async deleteRule(sucursalId: number, coverageId: number, ruleId: number): Promise<void> {
        await http.delete(`/sucursales/${sucursalId}/coverages/${coverageId}/rules/${ruleId}`)
    },

    // ── Importación masiva ────────────────────────────────────────────────────

    async importCoverages(sucursalId: number, file: File): Promise<ImportResult> {
        const formData = new FormData()
        formData.append('file', file)
        const response = await http.post(`/sucursales/${sucursalId}/coverages/import`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 60000,
        })
        return response.data?.data ?? response.data
    },

    async downloadTemplate(sucursalId: number): Promise<void> {
        const response = await http.get(`/sucursales/${sucursalId}/coverages/template`, {
            responseType: 'blob',
        })
        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = 'plantilla-coberturas-envio.xlsx'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
    },
}
